import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ToastrService } from 'ngx-toastr';
import {
  formatUserMessageText,
  isPdfStatusGenerating,
  logOrderDevDetail,
  ORDER_PDF_POLL_INTERVAL_MS,
  OrderPdfStatus,
  resolveUserMessage,
  triggerAttachmentDownload,
  UserMessage,
  UserMessageAction
} from 'src/entities/order';
import { normalizePdfStatus } from 'src/entities/order/model/order-status.model';
import {
  PurchaseNotePdfDownloadUrlResult,
  PurchaseNoteService,
  PURCHASE_NOTE_ERROR_MESSAGES,
  resolvePurchaseNoteGraphqlUserMessage
} from 'src/entities/purchase-note';

export interface PurchaseNotePdfDialogData {
  purchaseNoteId: string;
  purchaseNoteNumber?: string | number | null;
  pdfStatus?: string | null;
  lastPdfError?: string | null;
}

export interface PurchaseNotePdfDialogResult {
  pdfStatus?: string;
  pdfGeneratedAt?: string | null;
  lastPdfError?: string | null;
}

type ViewState = 'loading' | 'generating' | 'view' | 'error';

@Component({
  selector: 'app-purchase-note-pdf-dialog',
  standalone: true,
  templateUrl: './purchase-note-pdf-dialog.component.html',
  styleUrls: ['./purchase-note-pdf-dialog.component.css'],
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule
  ],
  host: { class: 'order-pdf-dialog-host' }
})
export class PurchaseNotePdfDialogComponent implements OnInit, OnDestroy {
  state: ViewState = 'loading';
  errorTitle: string | null = null;
  errorMessage: string | null = null;
  errorAction: UserMessageAction = 'none';
  downloading = false;
  retrying = false;

  private pollTimer: ReturnType<typeof setInterval> | null = null;
  safePdfUrl: SafeResourceUrl | null = null;

  constructor(
    private readonly dialogRef: MatDialogRef<PurchaseNotePdfDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PurchaseNotePdfDialogData,
    private readonly purchaseNoteService: PurchaseNoteService,
    private readonly sanitizer: DomSanitizer,
    private readonly toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.loadPdf();
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  get noteLabel(): string {
    const number = this.data.purchaseNoteNumber;
    return number != null && String(number).trim()
      ? `N° ${number}`
      : 'Orden de pedido';
  }

  get showRetryAction(): boolean {
    return this.state === 'error' && this.errorAction === 'retry';
  }

  get showRefreshAction(): boolean {
    return this.state === 'error' && this.errorAction === 'refresh';
  }

  downloadPdf(): void {
    if (this.downloading) {
      return;
    }

    this.downloading = true;

    this.purchaseNoteService
      .createPurchaseNotePdfDownloadUrl(this.data.purchaseNoteId, 'attachment')
      .subscribe({
        next: (result) => {
          this.downloading = false;

          if (
            normalizePdfStatus(result?.pdfStatus) === OrderPdfStatus.GENERATED &&
            result?.url
          ) {
            triggerAttachmentDownload(result.url);
            this.toastr.success('Descarga iniciada.');
            return;
          }

          if (normalizePdfStatus(result?.pdfStatus) === OrderPdfStatus.FAILED) {
            this.logPdfDevError(result);
            const msg = this.resolvePdfFailedMessage(result);
            this.toastr.error(msg.title, formatUserMessageText(msg));
            return;
          }

          this.toastr.error(
            'Descarga no disponible',
            'El PDF aún no está listo para descargar.'
          );
        },
        error: (error: unknown) => {
          this.downloading = false;
          const msg = resolvePurchaseNoteGraphqlUserMessage(
            error,
            'No se pudo descargar el PDF.'
          );
          this.toastr.error(msg.title, formatUserMessageText(msg));
        }
      });
  }

  close(): void {
    const status = normalizePdfStatus(this.data.pdfStatus ?? null);
    const result: PurchaseNotePdfDialogResult | undefined =
      status &&
      status !== OrderPdfStatus.PENDING &&
      status !== OrderPdfStatus.PROCESSING
        ? {
            pdfStatus: status,
            pdfGeneratedAt:
              status === OrderPdfStatus.GENERATED
                ? new Date().toISOString()
                : null,
            lastPdfError: this.data.lastPdfError ?? null
          }
        : undefined;

    this.dialogRef.close(result);
  }

  retry(): void {
    if (this.retrying) {
      return;
    }

    if (this.errorAction === 'refresh') {
      this.loadPdf();
      return;
    }

    this.retrying = true;
    this.state = 'loading';
    this.errorTitle = null;
    this.errorMessage = null;
    this.errorAction = 'none';
    this.safePdfUrl = null;
    this.stopPolling();

    this.purchaseNoteService
      .retryGeneratePurchaseNotePdf(this.data.purchaseNoteId)
      .subscribe({
        next: (result) => {
          this.retrying = false;
          this.data.pdfStatus = result.pdfStatus;
          this.data.lastPdfError = null;
          this.loadPdf();
        },
        error: (error: unknown) => {
          this.retrying = false;
          const msg = resolvePurchaseNoteGraphqlUserMessage(
            error,
            'No se pudo reintentar la generación del PDF.'
          );
          this.showUserError(msg);
        }
      });
  }

  private loadPdf(): void {
    this.state = 'loading';
    this.errorTitle = null;
    this.errorMessage = null;
    this.errorAction = 'none';
    this.requestPdfUrl();
  }

  private requestPdfUrl(): void {
    this.purchaseNoteService
      .createPurchaseNotePdfDownloadUrl(this.data.purchaseNoteId, 'inline')
      .subscribe({
        next: (result) => this.applyPdfResult(result),
        error: (error: unknown) => this.onRequestError(error)
      });
  }

  private applyPdfResult(
    result: PurchaseNotePdfDownloadUrlResult | null | undefined
  ): void {
    if (!result) {
      this.onRequestError();
      return;
    }

    this.data.pdfStatus = result.pdfStatus;
    this.data.lastPdfError = result.lastPdfError ?? null;

    const status = normalizePdfStatus(result.pdfStatus);

    switch (status) {
      case OrderPdfStatus.PENDING:
      case OrderPdfStatus.PROCESSING:
        this.state = 'generating';
        this.startPolling();
        return;

      case OrderPdfStatus.GENERATED:
        if (result.url) {
          this.showPdf(result.url);
          return;
        }
        this.showUserError(
          resolveUserMessage(
            null,
            [PURCHASE_NOTE_ERROR_MESSAGES],
            'El PDF está listo pero no se recibió la URL.'
          )
        );
        return;

      case OrderPdfStatus.FAILED:
        this.logPdfDevError(result);
        this.showUserError(this.resolvePdfFailedMessage(result));
        return;

      default:
        this.showUserError(
          resolveUserMessage(
            null,
            [PURCHASE_NOTE_ERROR_MESSAGES],
            'Estado del PDF desconocido.'
          )
        );
    }
  }

  private resolvePdfFailedMessage(
    result: PurchaseNotePdfDownloadUrlResult
  ): UserMessage {
    return resolveUserMessage(result.errorCode ?? 'PDF_GENERATION_FAILED', [
      PURCHASE_NOTE_ERROR_MESSAGES
    ]);
  }

  private logPdfDevError(result: PurchaseNotePdfDownloadUrlResult): void {
    logOrderDevDetail('purchaseNotePdf.lastPdfError', result.lastPdfError);
    logOrderDevDetail('purchaseNotePdf.errorCode', result.errorCode);
  }

  private showPdf(url: string): void {
    this.stopPolling();
    this.safePdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    this.state = 'view';
    this.errorTitle = null;
    this.errorMessage = null;
    this.errorAction = 'none';
    this.data.pdfStatus = OrderPdfStatus.GENERATED;
  }

  private showUserError(message: UserMessage): void {
    this.stopPolling();
    this.state = 'error';
    this.errorTitle = message.title;
    this.errorMessage = formatUserMessageText(message);
    this.errorAction = message.action ?? 'none';
    this.safePdfUrl = null;
  }

  private onRequestError(error?: unknown): void {
    const msg = resolvePurchaseNoteGraphqlUserMessage(
      error,
      'No se pudo consultar el estado del PDF.'
    );
    this.showUserError(msg);
  }

  private startPolling(): void {
    if (this.pollTimer) {
      return;
    }

    this.pollTimer = setInterval(() => {
      this.purchaseNoteService
        .createPurchaseNotePdfDownloadUrl(this.data.purchaseNoteId, 'inline')
        .subscribe({
          next: (result) => {
            if (!isPdfStatusGenerating(result?.pdfStatus)) {
              this.applyPdfResult(result);
            }
          },
          error: (error: unknown) => this.onRequestError(error)
        });
    }, ORDER_PDF_POLL_INTERVAL_MS);
  }

  private stopPolling(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }
}
